import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";

/**
 * Erkin matnli kiritish + takliflar (typeahead). Foydalanuvchi taklifni tanlashi
 * yoki butunlay yangi qiymat yozib qoldirishi mumkin (majburiy tanlov yo'q).
 * Takliflar `suffix`/case-insensitive, trimmed va takrorlanmas tarzda filtrlanadi.
 *
 * Manba ro'yxati (`suggestions`) chaqiruvchi tomonidan beriladi — har doim
 * workshop-scoped ma'lumotdan (masalan, shu ustaxona tovarlari kategoriyalari yoki
 * yetkazib beruvchilari). Komponent o'zi hech qanday so'rov yubormaydi.
 */
export function AutocompleteInput({
  value,
  onChange,
  suggestions,
  placeholder,
  disabled,
  required,
  id,
  className,
  inputMode,
}: {
  value: string;
  onChange: (v: string) => void;
  suggestions: string[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  className?: string;
  inputMode?: "text" | "search" | "tel" | "none";
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const matches = useMemo(() => {
    const s = value.trim().toLowerCase();
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of suggestions) {
      const v = (raw ?? "").trim();
      if (!v) continue;
      const k = v.toLowerCase();
      if (seen.has(k)) continue; // takrorlanmas (case-insensitive, trimmed)
      seen.add(k);
      if (s && !k.includes(s)) continue;
      out.push(v);
    }
    return out.slice(0, 8);
  }, [suggestions, value]);

  // Joriy qiymat aniq mavjud taklif bo'lsa, ortiqcha dropdown ko'rsatmaymiz.
  const exact = value.trim().toLowerCase();
  const show =
    open &&
    matches.length > 0 &&
    !(matches.length === 1 && matches[0].toLowerCase() === exact);

  return (
    <div ref={wrapRef} className="relative">
      <Input
        id={id}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={className}
        inputMode={inputMode}
        autoComplete="off"
      />
      {show && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 max-h-56 overflow-y-auto rounded-md border bg-card shadow-lg">
          {matches.map((m) => (
            <button
              key={m}
              type="button"
              className={cn(
                "flex w-full items-center px-3 py-2 text-left text-sm hover:bg-accent",
              )}
              onClick={() => {
                onChange(m);
                setOpen(false);
              }}
            >
              <span className="truncate">{m}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
